/* eslint-disable jsx-a11y/anchor-is-valid */
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper';
import Image, { StaticImageData } from 'next/image';
import { ReactNode } from 'react';
import { useState } from 'react';
import Link from 'next/link';
import StepOne from './images/step-1.png';
import StepTwo from './images/step-2.png';
import StepThree from './images/step-3.png';
import StepFour from './images/step-4.png';
import StepFive from './images/step-5.png';
import { ReactComponent as ArrowLeft } from './images/arrow-button-right.svg';
import { ReactComponent as ArrowRight } from './images/arrow-button-left.svg';
import { ReactComponent as ArrowLeftWithoutBorder } from './images/arrow-button-left-without-border.svg';
import { ReactComponent as ArrowRightWithoutBorder } from './images/arrow-button-right-without-border.svg';
import { ReactComponent as PlayButton } from './images/play-button.svg';
import { Description } from '@snapi/luxury';
import { Button } from '@snapi/luxury';
import { CloseIcon } from '@snapi/icons';
import clsx from 'clsx';

const Point = () => {
  return (
    <svg
      width="4"
      height="4"
      viewBox="0 0 4 4"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="2" cy="2" r="2" fill="#595959" />
    </svg>
  );
};

interface TestKitStepProps {
  index?: number;
  img: StaticImageData;
  description?: string;
  addition?: ReactNode;
  button?: ReactNode;
  pageForRedirect?: string;
  videoButton?: ReactNode;
}

const AllTestKitUseSteps: TestKitStepProps[] = [
  {
    img: StepFour,
    addition: (
      <div className="flex flex-col gap-2">
        <div className=" gap-2 flex">
          <div className="mt-[6px]">
            <Point />
          </div>
          <Description>
            Login or register at
            <a
              href="https://my.getsnapi.com/auth/register"
              aria-label="LinkInstruction"
              className="underline decoration-gray-soft cursor-pointer ml-1"
              target="_blank"
              rel="noreferrer"
            >
              my.getsnapi.com
            </a>
          </Description>
        </div>
        <div className="flex gap-2">
          <div className="mt-[6px]">
            <Point />
          </div>
          <Description>Click ‘Add new test’</Description>
        </div>
        <div className="flex gap-2">
          <div className="mt-[6px]">
            <Point />
          </div>
          <Description>
            Enter your Test Kit ID (ex. SNP-12345). This can be found on the
            test tube
          </Description>
        </div>
        <div className="flex gap-2">
          <div className="mt-[6px]">
            <Point />
          </div>
          <Description>
            Follow the instructions to complete our short questionnaire
          </Description>
        </div>
      </div>
    ),
    videoButton: (
      <a
        href="https://vimeo.com/844610002/5486038a08?share=copy"
        target="_blank"
        rel="noreferrer"
        className="flex sm:hidden mt-10 w-full"
      >
        <button className="min-w-[280px] h-[40px] uppercase font-semibold border-body text-body border-[1px] rounded-[40px] flex gap-10 justify-start items-center pl-2">
          <PlayButton />
          Watch the video
        </button>
      </a>
    ),
  },
  {
    img: StepOne,
    description:
      'Save a dirty diaper or soiled toilet paper sheet from your child for stool collection.',
  },
  {
    img: StepTwo,
    description:
      'Using the test kit, open the test tube and, using the swab, evenly smear a pea-sized sample onto the swab.',
    addition: (
      <div className="font-semibold mt-3 text-center lg:text-left">
        Heads up! Collecting too much poop may affect the results of the
        analysis. Make sure to collect the amount indicated in the photo. Save
        the test kit box to return your sample!
      </div>
    ),
  },
  {
    img: StepThree,
    description:
      "After collection, tightly close the cap of the test tube. If you haven't yet activated your test, do this now using your test tube ID.",
    addition: (
      <div className="font-semibold mt-3 text-center lg:text-left">
        Add your parent’s name on tube
      </div>
    ),
  },
  {
    img: StepFive,
    description:
      'Place the test tube back into the test kit box and mail your sample back to us in the pre-paid envelope provided. The test should be sent within two days after collection. The test should be kept in the fridge until you’re able to send it out. Please allow 2-4 weeks for our lab to process results.',
    button: (
      <div className="mt-10 flex justify-center lg:hidden">
        <Button size="lg" type="button">
          <span className="uppercase text-body font-semibold text-sm">
            Got it!
          </span>
        </Button>
      </div>
    ),
  },
];

const TestKitStep = ({
  index,
  img,
  description,
  addition,
  button,
  pageForRedirect,
  videoButton,
}: TestKitStepProps) => (
  <div className="flex flex-col items-center relative">
    <div className="px-5 lg:px-0 md:w-[600px] lg:w-[300px] relative">
      <Image src={img} />
      <div className="flex justify-center items-center absolute top-1 left-6 lg:left-1 bg-white border-[1px] border-gray-cream rounded-full w-10 h-10 z-20">
        <h5>{index}</h5>
      </div>
    </div>
    <div className="mt-5 px-5 lg:px-0 sm:w-[354px] md:w-[448px] text-center lg:text-left lg:w-[300px]">
      <Description> {description}</Description>
    </div>
    <div className="px-5 lg:px-0 sm:w-[354px] md:w-[448px] lg:w-[300px]">
      <Description>{addition}</Description>
    </div>
    {pageForRedirect && (
      <Link href={pageForRedirect}>
        <a>{button}</a>
      </Link>
    )}
    {videoButton && <div> {videoButton} </div>}
  </div>
);

interface InstructionPageProps {
  pageForRedirect: string;
}

export const InstructionPage = ({ pageForRedirect }: InstructionPageProps) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="flex flex-col relative bg-gray-beige h-max lg:h-full">
      <div className="lg:flex flex-col w-full max-w-[1700px] relative">
        <div className="absolute right-[20px] top-[-20px] md:right-8 lg:right-10">
          <Link href={pageForRedirect}>
            <a>
              <CloseIcon />
            </a>
          </Link>
        </div>
        <div className="flex justify-center lg:justify-start gap-3 items-center mb-8">
          <h1 className="text-body text-left lg:pl-12 xl:pl-[120px]">
            How to use
          </h1>
          <a
            href="https://vimeo.com/844610002/5486038a08?share=copy"
            target="_blank"
            rel="noreferrer"
            className="hidden sm:flex"
          >
            <button className="w-[200px] h-[40px] uppercase font-semibold border-body text-body border-[1px] rounded-[40px] flex gap-1 justify-center items-center">
              <PlayButton />
              Watch the video
            </button>
          </a>
        </div>

        <Swiper
          className="flex lg:hidden relative h-min"
          slidesPerView={1}
          spaceBetween={30}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          pagination={{
            enabled: true,
            clickable: true,
            type: 'bullets',
            bulletElement: 'span',
            el: '#instruction-pagination',
            bulletActiveClass: 'bg-body opacity-100',
          }}
          navigation={{
            enabled: true,
            nextEl: '#next-button-b',
            prevEl: '#prev-button-b',
          }}
          modules={[Navigation, Pagination]}
        >
          <>
            {AllTestKitUseSteps.map((item, index) => {
              return (
                <SwiperSlide
                  key={index}
                  className="mb-6 flex justify-center h-min"
                >
                  {(data) => {
                    return (
                      <div className="flex flex-col justify-start items-center sm:w-[400px]  md:w-[600px]">
                        <TestKitStep
                          {...item}
                          index={index + 1}
                          pageForRedirect={pageForRedirect}
                        />
                      </div>
                    );
                  }}
                </SwiperSlide>
              );
            })}
          </>
        </Swiper>

        <span
          id="prev-button-b"
          className="absolute top-[-260px] bottom-0 left-[14px] sm:left-[60px] xl:left-[150px] flex items-center z-10 cursor-pointer"
        >
          <ArrowLeftWithoutBorder
            className={clsx(
              activeIndex === 0 ? 'hidden' : 'flex w-10 h-10 lg:hidden'
            )}
          />
        </span>
        <span
          id="next-button-b"
          className="absolute top-[-260px] bottom-0 right-[14px] sm:right-[60px] flex items-center z-10 cursor-pointer"
        >
          <ArrowRightWithoutBorder
            className={clsx(
              activeIndex < 4 ? 'flex w-10 h-10 lg:hidden' : 'hidden'
            )}
          />
        </span>
        <div
          id="instruction-pagination"
          className={clsx(
            activeIndex === 4 ? 'hidden' : 'flex',
            'justify-center lg:hidden gap-1'
          )}
        ></div>

        <span
          id="prev-button"
          className="absolute top-[-200px] bottom-0 left-[76px] xl:left-[150px] flex items-center z-10 cursor-pointer"
        >
          <ArrowLeft
            className={clsx(
              activeIndex === 0
                ? 'hidden'
                : 'hidden lg:flex w-10 h-10 bg-white rounded-full shadow-lg'
            )}
          />
        </span>
        <span
          id="next-button"
          className="absolute top-[-200px] bottom-0 right-[60px] flex items-center z-10 cursor-pointer"
        >
          <ArrowRight
            className={clsx(
              activeIndex < 1
                ? 'hidden lg:flex w-10 h-10 bg-white rounded-full shadow-lg 2xl:hidden'
                : 'hidden'
            )}
          />
        </span>

        <Swiper
          className="hidden w-full lg:flex lg:pl-12 lg:pr-12 xl:pr-[120px] xl:pl-[120px] max-w-[1700px]"
          slidesPerView="auto"
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          spaceBetween={20}
          navigation={{
            enabled: true,
            nextEl: '#next-button',
            prevEl: '#prev-button',
          }}
          modules={[Navigation]}
        >
          <>
            {AllTestKitUseSteps.map((item, index) => {
              return (
                <SwiperSlide key={index} className="mb-12 w-[300px]">
                  {(data) => {
                    return (
                      <div className=" flex justify-start">
                        <TestKitStep {...item} index={index + 1} />
                      </div>
                    );
                  }}
                </SwiperSlide>
              );
            })}
          </>
        </Swiper>
      </div>
    </section>
  );
};
